/**
 * Utility functions for string manipulation
 */

/**
 * Extracts and parses JSON from a string that might contain extra text or be escaped.
 * @param input - The string potentially containing JSON
 * @returns The parsed JSON object or null if parsing fails
 */
export function extractAndParseJSON<T = any>(input: any): T | null {
  if (typeof input !== "string") {
    return null;
  }

  // Find the first '{'
  const start = input.indexOf("{");
  if (start === -1) {
    return null;
  }

  // Brace-counter walk to find the matching '}'
  let end = -1;
  let counter = 0;
  for (let i = start; i < input.length; i++) {
    if (input[i] === "{") {
      counter++;
    } else if (input[i] === "}") {
      counter--;
      if (counter === 0) {
        end = i;
        break;
      }
    }
  }

  if (end === -1) {
    return null;
  }

  const jsonStr = input.substring(start, end + 1);

  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    // If it fails, maybe the JSON was double-encoded/escaped in the string
    // This happens in some error response formats: "400 Bad Request: \"{\"code\":400,...}\""
    try {
      // Try to unescape by wrapping in JSON.parse if it's a quoted string
      if (
        jsonStr.startsWith('\\"') ||
        (input[start - 1] === '"' && input[end + 1] === '"')
      ) {
        const unescaped = JSON.parse(`"${jsonStr}"`);

        return JSON.parse(unescaped);
      }
    } catch (innerError) {
      // no-op
    }

    // Try to replace escaped quotes
    try {
      const simpleUnescape = jsonStr
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, "\\");

      return JSON.parse(simpleUnescape);
    } catch (lastError) {
      return null;
    }
  }
}
